import { getAuthorized } from "@/lib/fetch";
import i18n from "@/i18n";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { DisplayParams } from "../../data/displayParams";

type UseTableProps = {
  table_name?: string;
  sub_entity?: string;
  audit_table_name?: string;
  client: string;
  pagesizeDefault?: number;
  audit_id?: number | string;
  language?: string;
  disable_translations_request?: boolean;
};

type Translations = Record<string, Record<string, string>>;

type TableData<TData> = {
  data: TData[];
  count: number;
};

export function useTable<TData>({
  client,
  sub_entity,
  audit_table_name,
  table_name,
  pagesizeDefault = DisplayParams.DEFAULT_PAGE_SIZE_DEFAULT,
  audit_id,
  language,
  disable_translations_request = false,
}: UseTableProps) {
  const effectiveLanguage =
    language ?? (i18n.language?.startsWith("en") ? "en" : "fr");
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const queryParamsObj: Record<string, string | number | undefined> = {};
  queryParams.forEach((value, key) => {
    queryParamsObj[key] = value;
  });

  if (audit_id) {
    queryParamsObj["audit_id"] = audit_id;
  }

  if (sub_entity) {
    queryParamsObj["sub_entity"] = sub_entity;
  }
  const currentPage = Number(searchParams.get("page")) || 1;
  const pagesize = Number(searchParams.get("pagesize")) || pagesizeDefault;
  const tableQueryKey = audit_table_name
    ? [client, table_name, audit_table_name, "get", queryParamsObj]
    : [client, table_name, "get", queryParamsObj];

  const setCurrentPage = (value: React.SetStateAction<number>) => {
    const newPage = typeof value === "function" ? value(currentPage) : value;
    const newSearchParams = new URLSearchParams();

    searchParams.forEach((value, key) => {
      if (key !== "page" && key !== "pagesize") {
        newSearchParams.append(key, value);
      }
    });

    newSearchParams.set("page", newPage.toString());
    newSearchParams.set("pagesize", pagesize.toString());

    setSearchParams(newSearchParams);
  };

  // Fetch table data
  const { isLoading, error, data } = useQuery<TableData<TData>>({
    queryKey: tableQueryKey,
    queryFn: async () => {
      let url;
      if (audit_table_name) {
        url = `${
          import.meta.env.VITE_BACKEND_URL
        }/${client}/${audit_table_name}/audit?${buildQueryString(
          queryParamsObj,
        )}`;
      } else {
        url = `${
          import.meta.env.VITE_BACKEND_URL
        }/${client}/${table_name}?${buildQueryString(queryParamsObj)}`;
      }

      const res = await getAuthorized(url);
      return res.json();
    },
  });

  // Fetch enum translations (only when data is available)
  const { data: translations, isLoading: isLoadingTranslations } =
    useQuery<Translations>({
      queryKey: [table_name, "translations", effectiveLanguage],
      queryFn: async () => {
        const res = await getAuthorized(
          `${
            import.meta.env.VITE_BACKEND_URL
          }/${client}/${table_name}/translations?language=${effectiveLanguage}`,
        );
        return res.json();
      },
      enabled: !!data && !audit_table_name && !disable_translations_request, // Fetch translations only if data is available
    });

  // Apply translations to the table data
  const translatedData = useMemo(() => {
    if (!data || !("data" in data)) return [];

    const translatedData = data.data.map((row) => {
      const newRow = { ...row };

      if (!translations) return newRow;

      Object.entries(translations).forEach(([column, columnTranslations]) => {
        const value = row[column as keyof TData];

        const translationKey =
          typeof value === "string" || typeof value === "number"
            ? String(value)
            : null;

        if (
          translationKey &&
          columnTranslations[translationKey] !== undefined
        ) {
          newRow[column as keyof TData] = columnTranslations[
            translationKey
          ] as unknown as TData[keyof TData];
        }
      });

      return newRow;
    });

    return { count: data["count"], data: translatedData };
  }, [data, translations]);

  return {
    data: translatedData,
    isLoading: isLoading || isLoadingTranslations,
    error,
    currentPage,
    pagesize,
    setCurrentPage,
  };
}

const buildQueryString = (
  queryParams: Record<string, string | number | undefined>,
): string => {
  return Object.entries(queryParams)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("&");
};
